import React from 'react';
import {
  Flex,
  Box,
  Heading,
  Text,
  DataTable,
  LoadingOrbit,
  Card,
  CardHeader,
  CardContent,
  Button,
  Select,
  ComboBox,
  Divider,
  TagBadge,
  type SelectOption,
  type ComboBoxOption,
} from '@chatsdet/ui';
import type { ColumnDef, StockFeatures } from '@tanstack/react-table';
import {
  useOrganizationsQuery,
  useFeatureFlagsQuery,
  useCreateFlagMutation,
  useEnableFlagForOrgMutation,
  useDisableFlagForOrgMutation,
  useEnableFlagForUserMutation,
  useDisableFlagForUserMutation,
  useSearchUsersQuery,
  useSetFeatureFlagLifecycleMutation,
  type FeatureFlag,
} from './useSuperadminData';

const lifecycleOptions = [
  { id: 'active', label: 'Active' },
  { id: 'baking', label: 'Baking (the oven)' },
  { id: 'internal', label: 'Internal (internal-only)' },
  { id: 'deprecated', label: 'Deprecated' },
  { id: 'ready_for_cleanup', label: 'Ready for Cleanup' },
] satisfies SelectOption[];

const DEFAULT_BAKE_REVIEW_INTERVAL_DAYS = 7;
const DEFAULT_BAKE_MAX_WINDOW_DAYS = 30;
const DAY_MS = 24 * 60 * 60 * 1000;

/** Bake age (days) + burning state from a flag's bake metadata. */
const bakeStatus = (flag: Pick<FeatureFlag, 'bakeStartedAt' | 'bakeMaxWindowDays'>) => {
  if (!flag.bakeStartedAt) return { ageDays: 0, burning: false };
  const ageMs = Date.now() - new Date(flag.bakeStartedAt).getTime();
  const maxWindow = flag.bakeMaxWindowDays ?? DEFAULT_BAKE_MAX_WINDOW_DAYS;
  return { ageDays: Math.floor(ageMs / DAY_MS), burning: ageMs > maxWindow * DAY_MS };
};

const booleanOptions = [
  { id: 'true', label: 'true' },
  { id: 'false', label: 'false' },
] satisfies SelectOption[];

const toLifecycleLabel = (stage: FeatureFlag['lifecycleStage']) => {
  switch (stage) {
    case 'baking':
      return 'Baking';
    case 'internal':
      return 'Internal';
    case 'deprecated':
      return 'Deprecated';
    case 'ready_for_cleanup':
      return 'Ready for cleanup';
    default:
      return 'Active';
  }
};

export function SuperadminFeatureFlags() {
  const [createFlagName, setCreateFlagName] = React.useState('');
  const [createFlagDescription, setCreateFlagDescription] = React.useState('');
  const [createFlagError, setCreateFlagError] = React.useState<string | null>(null);
  const [selectedFlag, setSelectedFlag] = React.useState<FeatureFlag | null>(null);
  const [selectedOrgForFlag, setSelectedOrgForFlag] = React.useState<string>('');
  const [orgSearchInput, setOrgSearchInput] = React.useState('');
  const [userSearchInput, setUserSearchInput] = React.useState('');
  const [selectedUserForFlag, setSelectedUserForFlag] = React.useState<string | null>(null);
  const [lifecycleStage, setLifecycleStage] =
    React.useState<FeatureFlag['lifecycleStage']>('active');
  const [lifecycleFinalValue, setLifecycleFinalValue] = React.useState<'true' | 'false'>('false');
  const [lifecycleError, setLifecycleError] = React.useState<string | null>(null);
  const [bakePlan, setBakePlan] = React.useState('');
  const [bakeReviewInterval, setBakeReviewInterval] = React.useState(
    String(DEFAULT_BAKE_REVIEW_INTERVAL_DAYS)
  );
  const [bakeMaxWindow, setBakeMaxWindow] = React.useState(String(DEFAULT_BAKE_MAX_WINDOW_DAYS));

  const organizationsQuery = useOrganizationsQuery({ search: orgSearchInput, pageSize: 20 });
  const featureFlagsQuery = useFeatureFlagsQuery();
  const createFlagMutation = useCreateFlagMutation();
  const enableFlagForOrgMutation = useEnableFlagForOrgMutation();
  const disableFlagForOrgMutation = useDisableFlagForOrgMutation();
  const enableFlagForUserMutation = useEnableFlagForUserMutation();
  const disableFlagForUserMutation = useDisableFlagForUserMutation();
  const userSearchQuery = useSearchUsersQuery(userSearchInput);
  const setFeatureFlagLifecycleMutation = useSetFeatureFlagLifecycleMutation();

  React.useEffect(() => {
    if (!selectedFlag || !featureFlagsQuery.data?.flags) {
      return;
    }
    const fresh = featureFlagsQuery.data.flags.find((flag) => flag.name === selectedFlag.name);
    if (fresh) {
      setSelectedFlag(fresh);
    }
  }, [selectedFlag, featureFlagsQuery.data]);

  React.useEffect(() => {
    if (!selectedFlag) {
      setLifecycleStage('active');
      setLifecycleFinalValue('false');
      return;
    }

    setLifecycleStage(selectedFlag.lifecycleStage);
    if (selectedFlag.finalValue === true) {
      setLifecycleFinalValue('true');
    } else if (selectedFlag.finalValue === false) {
      setLifecycleFinalValue('false');
    }
    setBakePlan(selectedFlag.bakeMonitoringPlan ?? '');
    setBakeReviewInterval(
      String(selectedFlag.bakeReviewIntervalDays ?? DEFAULT_BAKE_REVIEW_INTERVAL_DAYS)
    );
    setBakeMaxWindow(String(selectedFlag.bakeMaxWindowDays ?? DEFAULT_BAKE_MAX_WINDOW_DAYS));
  }, [selectedFlag]);

  const handleCreateFlag = () => {
    setCreateFlagError(null);
    const name = createFlagName.trim();
    if (!name) {
      setCreateFlagError('Feature flag name is required');
      return;
    }
    const description = createFlagDescription.trim() || undefined;
    createFlagMutation.mutate(
      { name, description },
      {
        onSuccess: () => {
          setCreateFlagName('');
          setCreateFlagDescription('');
          setCreateFlagError(null);
        },
      }
    );
  };

  const handleEnableFlagForOrg = () => {
    if (!selectedFlag || !selectedOrgForFlag) return;
    enableFlagForOrgMutation.mutate(
      {
        flagName: selectedFlag.name,
        orgId: selectedOrgForFlag,
      },
      {
        onSuccess: () => {
          setSelectedOrgForFlag('');
        },
      }
    );
  };

  const handleDisableFlagForOrg = (flagName: string, orgId: string) => {
    disableFlagForOrgMutation.mutate({ flagName, orgId });
  };

  const handleEnableFlagForUser = () => {
    if (!selectedFlag || !selectedUserForFlag) return;
    enableFlagForUserMutation.mutate(
      {
        flagName: selectedFlag.name,
        userId: selectedUserForFlag,
      },
      {
        onSuccess: () => {
          setSelectedUserForFlag(null);
          setUserSearchInput('');
        },
      }
    );
  };

  const handleDisableFlagForUser = (flagName: string, userId: string) => {
    disableFlagForUserMutation.mutate({ flagName, userId });
  };

  const handleSaveLifecycle = () => {
    if (!selectedFlag) {
      setLifecycleError('Select a feature flag first');
      return;
    }

    setLifecycleError(null);

    // active, baking and internal are gate-driven (no final value).
    const gateDriven =
      lifecycleStage === 'active' || lifecycleStage === 'baking' || lifecycleStage === 'internal';
    const finalValue = gateDriven ? undefined : lifecycleFinalValue === 'true';

    if (lifecycleStage === 'baking') {
      if (!selectedFlag.productCardId) {
        setLifecycleError('Link this flag to a product card before putting it in the oven.');
        return;
      }
      if (!bakePlan.trim() && !selectedFlag.bakeMonitoringPlan) {
        setLifecycleError('A monitoring/telemetry plan is required to enter the oven.');
        return;
      }
    }

    const interval = Number.parseInt(bakeReviewInterval, 10);
    const maxWindow = Number.parseInt(bakeMaxWindow, 10);

    setFeatureFlagLifecycleMutation.mutate(
      {
        flagName: selectedFlag.name,
        stage: lifecycleStage,
        finalValue,
        ...(lifecycleStage === 'baking'
          ? {
              monitoringPlan: bakePlan.trim() || undefined,
              reviewIntervalDays: Number.isInteger(interval) && interval > 0 ? interval : undefined,
              maxWindowDays: Number.isInteger(maxWindow) && maxWindow > 0 ? maxWindow : undefined,
            }
          : {}),
      },
      {
        onSuccess: () => {
          setLifecycleError(null);
        },
        onError: (error) => {
          setLifecycleError(
            error instanceof Error ? error.message : 'Failed to update lifecycle metadata'
          );
        },
      }
    );
  };

  const userOptionsForFlag: ComboBoxOption[] = React.useMemo(() => {
    const searchResults = userSearchQuery.data?.users ?? [];
    const existingUserIds = new Set(selectedFlag?.users.map((u) => u.userId) ?? []);
    return searchResults
      .filter((u) => !existingUserIds.has(u.id))
      .map((u) => ({
        id: u.id,
        label: u.email,
        description: u.name ?? undefined,
      }));
  }, [userSearchQuery.data, selectedFlag]);

  const orgOptionsForFlag: ComboBoxOption[] = React.useMemo(() => {
    const orgs = organizationsQuery.data?.organizations ?? [];
    const enabledOrgIds = new Set(selectedFlag?.organizations.map((o) => o.orgId) ?? []);
    return orgs
      .filter((org) => !enabledOrgIds.has(org.id))
      .map((org) => ({ id: org.id, label: org.name }));
  }, [organizationsQuery.data, selectedFlag]);

  const featureFlagColumns: ColumnDef<StockFeatures, FeatureFlag>[] = React.useMemo(
    () => [
      {
        accessorKey: 'name',
        header: 'Flag Name',
        cell: ({ row }) => <Text weight="medium">{row.original.name}</Text>,
      },
      {
        accessorKey: 'description',
        header: 'Description',
        cell: ({ row }) => (
          <Text size="sm" className="text-muted-foreground">
            {row.original.description || '—'}
          </Text>
        ),
      },
      {
        accessorKey: 'lifecycleStage',
        header: 'Lifecycle',
        cell: ({ row }) => {
          const baking = row.original.lifecycleStage === 'baking';
          const { ageDays, burning } = baking
            ? bakeStatus(row.original)
            : { ageDays: 0, burning: false };
          return (
            <Flex gap="sm" align="center">
              <Text size="sm" className="text-muted-foreground">
                {toLifecycleLabel(row.original.lifecycleStage)}
                {baking && ` · ${ageDays}d`}
              </Text>
              {burning && <TagBadge name="🔥 Burning" color="orange" hideDot />}
              {row.original.isStale && <TagBadge name="Stale" color="yellow" hideDot />}
            </Flex>
          );
        },
      },
      {
        accessorKey: 'organizations',
        header: 'Gates',
        cell: ({ row }) => (
          <TagBadge
            name={
              <>
                {row.original.organizations.length} org(s), {row.original.users.length} user(s)
              </>
            }
            color="gray"
            hideDot
          />
        ),
      },
      {
        accessorKey: 'createdAt',
        header: 'Created',
        cell: ({ row }) => (
          <Text size="sm" className="text-muted-foreground">
            {new Date(row.original.createdAt).toLocaleDateString()}
          </Text>
        ),
      },
    ],
    []
  );

  return (
    <Box className="space-y-6">
      <Flex direction="column" gap="xl">
        <Card padding="lg">
          <CardHeader>
            <Heading level={3}>Create Feature Flag</Heading>
            <Text size="sm" className="text-muted-foreground mt-1">
              Add a new feature flag to control feature access across organizations.
            </Text>
          </CardHeader>
          <CardContent>
            <Flex direction="column" gap="lg">
              <Flex gap="md" wrap>
                <Box className="min-w-[200px]">
                  <Text size="xs" className="text-muted-foreground mb-1 block">
                    Flag Name
                  </Text>
                  <input
                    value={createFlagName}
                    onChange={(event) => setCreateFlagName(event.target.value)}
                    className="w-full border border-border rounded-md px-3 py-2 text-sm bg-background"
                    placeholder="e.g., advanced-analytics"
                  />
                </Box>
                <Box className="min-w-[300px]">
                  <Text size="xs" className="text-muted-foreground mb-1 block">
                    Description (optional)
                  </Text>
                  <input
                    value={createFlagDescription}
                    onChange={(event) => setCreateFlagDescription(event.target.value)}
                    className="w-full border border-border rounded-md px-3 py-2 text-sm bg-background"
                    placeholder="What does this feature enable?"
                  />
                </Box>
              </Flex>

              {createFlagError && (
                <Text className="text-destructive text-sm">{createFlagError}</Text>
              )}
              {createFlagMutation.isError && (
                <Text className="text-destructive text-sm">
                  {createFlagMutation.error instanceof Error
                    ? createFlagMutation.error.message
                    : 'Failed to create feature flag'}
                </Text>
              )}
              {createFlagMutation.isSuccess && (
                <Text className="text-success text-sm">Feature flag created successfully!</Text>
              )}

              <Button
                onPress={handleCreateFlag}
                isDisabled={createFlagMutation.isPending || !createFlagName.trim()}
              >
                {createFlagMutation.isPending ? 'Creating...' : 'Create Flag'}
              </Button>
            </Flex>
          </CardContent>
        </Card>

        <Card padding="lg">
          <CardHeader>
            <Flex align="center" justify="between" wrap gap="md">
              <div>
                <Heading level={3}>Feature Flags</Heading>
                <Text size="sm" className="text-muted-foreground mt-1">
                  Manage feature flags and organization access across the platform.
                </Text>
              </div>
              {featureFlagsQuery.isLoading && <LoadingOrbit size="sm" />}
            </Flex>
          </CardHeader>
          <CardContent>
            {featureFlagsQuery.isLoading ? (
              <Flex align="center" justify="center" className="py-10">
                <LoadingOrbit size="lg" />
              </Flex>
            ) : featureFlagsQuery.isError ? (
              <Text className="text-destructive">
                {featureFlagsQuery.error instanceof Error
                  ? featureFlagsQuery.error.message
                  : 'Failed to load feature flags'}
              </Text>
            ) : featureFlagsQuery.data?.flags?.length ? (
              <Flex direction="column" gap="lg">
                <DataTable
                  columns={featureFlagColumns}
                  data={featureFlagsQuery.data.flags}
                  onRowClick={(flag) => setSelectedFlag(flag)}
                />

                {selectedFlag && (
                  <>
                    <Divider />
                    <Box>
                      <Heading level={4} className="text-base mb-3">
                        Manage "{selectedFlag.name}"
                      </Heading>
                      {selectedFlag.description && (
                        <Text size="sm" className="text-muted-foreground mb-4">
                          {selectedFlag.description}
                        </Text>
                      )}

                      <Box className="border border-border rounded-md p-4 mb-6">
                        <Heading level={5} className="text-sm mb-2">
                          Lifecycle
                        </Heading>
                        <Text size="sm" className="text-muted-foreground mb-4">
                          Put a flag in the oven (baking) to monitor it, or mark it deprecated /
                          ready for cleanup with its final boolean value.
                        </Text>

                        <Flex gap="md" wrap align="end">
                          <Box className="min-w-[220px]">
                            <Text size="xs" className="text-muted-foreground mb-1 block">
                              Stage
                            </Text>
                            <Select
                              items={lifecycleOptions}
                              selectedKey={lifecycleStage}
                              onSelectionChange={(key) =>
                                setLifecycleStage(String(key) as FeatureFlag['lifecycleStage'])
                              }
                            />
                          </Box>

                          {lifecycleStage !== 'active' &&
                            lifecycleStage !== 'baking' &&
                            lifecycleStage !== 'internal' && (
                              <Box className="min-w-[200px]">
                                <Text size="xs" className="text-muted-foreground mb-1 block">
                                  Final Value
                                </Text>
                                <Select
                                  items={booleanOptions}
                                  selectedKey={lifecycleFinalValue}
                                  onSelectionChange={(key) =>
                                    setLifecycleFinalValue(String(key) as 'true' | 'false')
                                  }
                                />
                              </Box>
                            )}

                          <Button
                            variant="outline"
                            onPress={handleSaveLifecycle}
                            isDisabled={setFeatureFlagLifecycleMutation.isPending}
                          >
                            {setFeatureFlagLifecycleMutation.isPending
                              ? 'Saving lifecycle...'
                              : 'Save Lifecycle'}
                          </Button>
                        </Flex>

                        {lifecycleStage === 'baking' && (
                          <Box className="mt-4">
                            {!selectedFlag.productCardId && (
                              <Text className="text-destructive text-sm mb-2">
                                Link this flag to a product card (below) before putting it in the
                                oven.
                              </Text>
                            )}
                            <Text size="xs" className="text-muted-foreground mb-1 block">
                              Monitoring / telemetry plan (required) — what to watch +
                              success/rollback criteria
                            </Text>
                            <textarea
                              value={bakePlan}
                              onChange={(event) => setBakePlan(event.target.value)}
                              rows={5}
                              className="w-full border border-border rounded-md px-3 py-2 text-sm bg-background font-mono"
                              placeholder={
                                'e.g.\n- Dashboard: <link>\n- Watch: error rate < 1%, p95 < 400ms\n- Roll back if: 5xx spike or error budget burn'
                              }
                            />
                            <Flex gap="md" wrap className="mt-2">
                              <Box className="min-w-[160px]">
                                <Text size="xs" className="text-muted-foreground mb-1 block">
                                  Review interval (days)
                                </Text>
                                <input
                                  type="number"
                                  min={1}
                                  value={bakeReviewInterval}
                                  onChange={(event) => setBakeReviewInterval(event.target.value)}
                                  className="w-full border border-border rounded-md px-3 py-2 text-sm bg-background"
                                />
                              </Box>
                              <Box className="min-w-[160px]">
                                <Text size="xs" className="text-muted-foreground mb-1 block">
                                  Max window before burning (days)
                                </Text>
                                <input
                                  type="number"
                                  min={1}
                                  value={bakeMaxWindow}
                                  onChange={(event) => setBakeMaxWindow(event.target.value)}
                                  className="w-full border border-border rounded-md px-3 py-2 text-sm bg-background"
                                />
                              </Box>
                            </Flex>
                          </Box>
                        )}

                        {lifecycleError && (
                          <Text className="text-destructive text-sm mt-3">{lifecycleError}</Text>
                        )}

                        <Text size="xs" className="text-muted-foreground mt-3">
                          Current: {toLifecycleLabel(selectedFlag.lifecycleStage)}
                          {selectedFlag.lifecycleStage === 'baking' &&
                            (() => {
                              const { ageDays, burning } = bakeStatus(selectedFlag);
                              return (
                                <>
                                  {' '}
                                  (baked {ageDays}d{burning ? ' — 🔥 burning' : ''})
                                </>
                              );
                            })()}
                          {selectedFlag.lifecycleStage !== 'active' &&
                            selectedFlag.lifecycleStage !== 'baking' &&
                            selectedFlag.lifecycleStage !== 'internal' && (
                              <> (final: {selectedFlag.finalValue === true ? 'true' : 'false'})</>
                            )}
                        </Text>
                      </Box>

                      <Flex gap="md" wrap align="end" className="mb-6">
                        <ComboBox
                          label="Enable for organization"
                          placeholder="Search organizations"
                          items={orgOptionsForFlag}
                          inputValue={orgSearchInput}
                          onInputChange={setOrgSearchInput}
                          selectedKey={selectedOrgForFlag || null}
                          onSelectionChange={(key) => setSelectedOrgForFlag(key ? String(key) : '')}
                          isLoading={organizationsQuery.isFetching}
                          className="min-w-[240px]"
                          defaultFilter={() => true}
                        />
                        <Button
                          onPress={handleEnableFlagForOrg}
                          isDisabled={
                            enableFlagForOrgMutation.isPending ||
                            !selectedOrgForFlag ||
                            organizationsQuery.isLoading
                          }
                          size="sm"
                        >
                          {enableFlagForOrgMutation.isPending ? 'Enabling...' : 'Enable'}
                        </Button>
                      </Flex>

                      {enableFlagForOrgMutation.isError && (
                        <Text className="text-destructive text-sm mb-4">
                          {enableFlagForOrgMutation.error instanceof Error
                            ? enableFlagForOrgMutation.error.message
                            : 'Failed to enable feature flag'}
                        </Text>
                      )}

                      <Heading level={5} className="text-sm mb-3">
                        Organizations with Access
                      </Heading>
                      {selectedFlag.organizations.length > 0 ? (
                        <Flex direction="column" gap="sm">
                          {selectedFlag.organizations.map((org) => (
                            <Flex
                              key={org.gateId}
                              align="center"
                              justify="between"
                              className="p-3 border border-border rounded-md"
                            >
                              <Flex direction="column" gap="xs">
                                <Text weight="medium">{org.orgName}</Text>
                                <Text size="xs" className="text-muted-foreground">
                                  Enabled on {new Date(org.createdAt).toLocaleString()}
                                </Text>
                              </Flex>
                              <Button
                                variant="destructive"
                                size="sm"
                                onPress={() =>
                                  handleDisableFlagForOrg(selectedFlag.name, org.orgId)
                                }
                                isDisabled={disableFlagForOrgMutation.isPending}
                              >
                                Remove
                              </Button>
                            </Flex>
                          ))}
                        </Flex>
                      ) : (
                        <Text size="sm" className="text-muted-foreground">
                          No organizations have access to this feature yet.
                        </Text>
                      )}

                      <Divider className="my-6" />

                      <Flex gap="md" wrap align="end" className="mb-6">
                        <ComboBox
                          label="Enable for user"
                          placeholder="Search by name or email"
                          items={userOptionsForFlag}
                          inputValue={userSearchInput}
                          onInputChange={setUserSearchInput}
                          selectedKey={selectedUserForFlag}
                          onSelectionChange={(key) => setSelectedUserForFlag(key as string | null)}
                          isLoading={userSearchQuery.isFetching}
                          className="min-w-[300px]"
                          defaultFilter={() => true}
                        />
                        <Button
                          onPress={handleEnableFlagForUser}
                          isDisabled={enableFlagForUserMutation.isPending || !selectedUserForFlag}
                          size="sm"
                        >
                          {enableFlagForUserMutation.isPending ? 'Enabling...' : 'Enable'}
                        </Button>
                      </Flex>

                      {enableFlagForUserMutation.isError && (
                        <Text className="text-destructive text-sm mb-4">
                          {enableFlagForUserMutation.error instanceof Error
                            ? enableFlagForUserMutation.error.message
                            : 'Failed to enable feature flag for user'}
                        </Text>
                      )}

                      <Heading level={5} className="text-sm mb-3">
                        Users with Access
                      </Heading>
                      {selectedFlag.users.length > 0 ? (
                        <Flex direction="column" gap="sm">
                          {selectedFlag.users.map((user) => (
                            <Flex
                              key={user.gateId}
                              align="center"
                              justify="between"
                              className="p-3 border border-border rounded-md"
                            >
                              <Flex direction="column" gap="xs">
                                <Text weight="medium">{user.userEmail ?? user.userId}</Text>
                                <Text size="xs" className="text-muted-foreground">
                                  Enabled on {new Date(user.createdAt).toLocaleString()}
                                </Text>
                              </Flex>
                              <Button
                                variant="destructive"
                                size="sm"
                                onPress={() =>
                                  handleDisableFlagForUser(selectedFlag.name, user.userId)
                                }
                                isDisabled={disableFlagForUserMutation.isPending}
                              >
                                Remove
                              </Button>
                            </Flex>
                          ))}
                        </Flex>
                      ) : (
                        <Text size="sm" className="text-muted-foreground">
                          No users have individual access to this feature yet.
                        </Text>
                      )}
                    </Box>
                  </>
                )}
              </Flex>
            ) : (
              <Text className="text-muted-foreground">
                No feature flags found. Create one to get started.
              </Text>
            )}
          </CardContent>
        </Card>
      </Flex>
    </Box>
  );
}
